// FinancialRatios.tsx with frontend calculation + backend sync
// This follows the same pattern as UnifiedDetailedIncomeStatement.tsx and UnifiedDetailedBalanceSheet.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import { AccountEntry, CategoryTemplate, Project, ViewType } from '../../types';
import { ViewToggle } from '../ui/ViewToggle';
import { getSupabase } from '../../lib/api/supabase';
import { 
  getLineItemValue as getLineItemValueHelper, 
  getAllFinancialReports,
  compareMonthYear
} from '../../lib/data/financialReportsHelper';
import { getDateForPeriod } from '../../lib/utils/dateMapping';
import { FrontendRatiosCalculationService } from '../../lib/financial/frontendRatiosCalculations';
import { FrontendPLCalculationService } from '../../lib/financial/frontendPLCalculations';
import { FrontendBSCalculationService } from '../../lib/financial/frontendBSCalculations';
import { FrontendCFCalculationService } from '../../lib/financial/frontendCFCalculations';
import { FrontendNWCCalculationService } from '../../lib/financial/frontendNWCCalculations';

// Enhanced line item interface for backend storage
interface RatioLineItem {
  name: string;
  type: 'ratio' | 'section_header';
  level: number;
  formula?: string;
  description?: string;
  account_mappings?: string[];
  values: (number | null)[];
  category?: string;
  format?: 'percentage' | 'decimal' | 'multiple' | 'days';
}

interface FinancialRatiosProps {
  accounts: AccountEntry[];
  activeTemplate?: CategoryTemplate | null;
  selectedProject?: Project | null;
  plPeriodType?: string;
}

const periodOptionsByView = {
  monthly: [
    { value: 'LMA', label: 'Last Month (Annualized)' },
    { value: 'L3MA', label: 'Last 3 Months (Annualized)' },
    { value: 'LTM', label: 'Last 12 Months' },
  ],
  quarterly: [
    { value: 'LQA', label: 'Last Quarter (Annualized)' },
    { value: 'LTM', label: 'Last 12 Months' },
  ],
  yearly: [
    { value: 'LTM', label: 'Last 12 Months' },
  ],
};


// ViewType now imported from types.ts
type PeriodType = 'LMA' | 'L3MA' | 'LQA' | 'LTM';

// --- RatioTooltip component (restored from old version) ---
const RatioTooltip = ({ calculation }: { calculation: any }) => (
  <span className="ml-1 cursor-help" title={calculation.calculation || calculation.formula}>
    <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 inline" />
  </span>
);

export const FinancialRatios: React.FC<FinancialRatiosProps> = ({ 
  accounts, 
  activeTemplate, 
  selectedProject
}) => {
  // Early return if no template or project
  if (!activeTemplate || !selectedProject) {
    return (
      <div className="p-8 text-center text-lg text-gray-600">
        Please select an active template and project to view financial ratios.
      </div>
    );
  }

  // State management (restored period type state)
  const [viewType, setViewType] = useState<ViewType>('monthly');
  const [periodType, setPeriodType] = useState<PeriodType>('LMA');
  const [financialReports, setFinancialReports] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calculationSource] = useState<'frontend' | 'backend'>('frontend');

  // Debug: Log periodType changes
  useEffect(() => {
    console.log(`🔄 [SIMPLE DEBUG] PeriodType changed to: ${periodType}`);
  }, [periodType]);

  // Transform Supabase report data to expected format
  function transformReportData(rawReport: any) {
    if (!rawReport?.data) {
      return rawReport;
    }
    
    if (rawReport.report_type === 'nwc_detail') {
      return rawReport;
    }
    
    // Handle cash flow report structure (sections with nested line_items)
    if (rawReport.report_type === 'cf_detail' && rawReport.data.sections) {
      const { periods, sections } = rawReport.data;
      const data: Record<string, Record<string, number>> = {};
      
      // Flatten all line items from all sections
      for (const section of sections) {
        if (section.line_items) {
          for (const item of section.line_items) {
            if (item.values && item.name) {
              data[item.name] = {};
              periods.forEach((period: string, idx: number) => {
                data[item.name][period] = item.values[idx];
              });
            }
          }
        }
      }
      
      return { ...rawReport, data };
    }
    
    // Handle standard report structure (flat line_items)
    if (!rawReport.data.periods || !rawReport.data.line_items) {
      return rawReport;
    }
    
    const { periods, line_items } = rawReport.data;
    const data: Record<string, Record<string, number>> = {};
    
    for (const item of line_items) {
      if (item.values && item.name) {
        data[item.name] = {};
        periods.forEach((period: string, idx: number) => {
          data[item.name][period] = item.values[idx];
        });
      }
    }
    
    return { ...rawReport, data };
  }

  // Frontend calculation memoization
  const frontendRatiosData = useMemo(() => {
    if (!activeTemplate || accounts.length === 0) return null;
    
    console.log('[Ratios Frontend] Calculating ratios data with frontend service');
    return FrontendRatiosCalculationService.calculate(
      accounts, 
      activeTemplate, 
      viewType, 
      selectedProject?.period_type, 
      selectedProject
    );
  }, [accounts, activeTemplate, viewType, selectedProject]);

  // Dynamic data source selection - must be declared before useEffect
  const currentDataSource = useMemo(() => {
    if (calculationSource === 'frontend' && frontendRatiosData) {
      console.log('✅ [SIMPLE] Using FRONTEND calculations');
      return frontendRatiosData;
    } else {
      console.log('❌ [SIMPLE] Using BACKEND calculations - THIS IS WRONG!');
      return financialReports;
    }
  }, [calculationSource, frontendRatiosData, financialReports]);

  // Load financial reports from backend - only when needed
  useEffect(() => {
    if (!selectedProject?.id) return;
    
    // Only load backend data if calculation source is 'backend' 
    if (calculationSource === 'backend') {
      setLoading(true);
      setError(null);
      
      const loadReports = async () => {
        try {
          const reports = await getAllFinancialReports(selectedProject.id);
          const transformedReports: any = {};
          for (const key of Object.keys(reports)) {
            if (['cf_detail', 'nwc_detail', 'pnl_detail', 'bs_detail', 'balance_sheet'].includes(key)) {
              transformedReports[key] = transformReportData(reports[key]);
            }
          }
          setFinancialReports(transformedReports);
          
          if (!transformedReports.pnl_detail && !transformedReports.bs_detail && !transformedReports.cf_detail) {
            setError('No financial reports found. Please generate reports first.');
          }
        } catch (err) {
          console.error('Error loading financial reports:', err);
          setError('Failed to load financial reports. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      loadReports();
    } else {
      // Frontend data is available, no need to load backend
      setLoading(false);
      setError(null);
    }
  }, [selectedProject?.id, calculationSource, frontendRatiosData]);

  // Helper function to get periods for calculation based on periodType
  const getCalculationPeriods = (currentPeriod: string): string[] => {
    const sortedPeriods = [...availablePeriods].sort(compareMonthYear);
    const currentIndex = sortedPeriods.indexOf(currentPeriod);
    if (currentIndex === -1) return [currentPeriod];

    let result: string[];
    switch (periodType) {
      case 'LMA': // Last Month (Annualized) - just the current period
        result = [currentPeriod];
        break;
      case 'L3MA': // Last 3 Months (Annualized) - current period + 2 previous
        result = sortedPeriods.slice(Math.max(0, currentIndex - 2), currentIndex + 1);
        break;
      case 'LQA': // Last Quarter (Annualized) - full quarter
        if (selectedProject?.period_type === 'monthly' && viewType === 'quarterly') {
          // Scenario 4: Monthly backend data, quarterly view - get 3 months of the quarter
          const [monthStr, yearStr] = currentPeriod.split('-');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIndex = monthNames.indexOf(monthStr);

          if (monthIndex !== -1) {
            // Calculate the quarter start month (0, 3, 6, or 9)
            const quarterStartIndex = Math.floor(monthIndex / 3) * 3;

            // Get the 3 months in this quarter
            const quarterMonths = [];
            for (let i = 0; i < 3; i++) {
              const qMonthIndex = quarterStartIndex + i;
              const qMonthName = monthNames[qMonthIndex];
              const qMonthPeriod = `${qMonthName}-${yearStr}`;

              // Only include if this month exists in our available data
              if (sortedPeriods.includes(qMonthPeriod)) {
                quarterMonths.push(qMonthPeriod);
              }
            }
            result = quarterMonths;
          } else {
            // Fallback to current period only
            result = [currentPeriod];
          }
        } else if (selectedProject?.period_type === 'quarterly') {
          // Scenario 6: Quarterly backend data, quarterly view - use current quarter only
          result = [currentPeriod];
        } else {
          // Other scenarios - use current period only
          result = [currentPeriod];
        }
        break;
      case 'LTM': // Last 12 Months (period-type aware)
        if (selectedProject?.period_type === 'monthly') {
          // Scenarios 1-5: Monthly backend data - get 12 months
          result = sortedPeriods.slice(Math.max(0, currentIndex - 11), currentIndex + 1);
        } else if (selectedProject?.period_type === 'quarterly') {
          // Scenario 7: Quarterly backend data - get 4 quarters (not 12)
          result = sortedPeriods.slice(Math.max(0, currentIndex - 3), currentIndex + 1);
        } else if (selectedProject?.period_type === 'yearly') {
          // Scenario 8: Yearly backend data - get 1 year (current year only)
          result = [currentPeriod];
        } else {
          // Fallback - assume monthly
          result = sortedPeriods.slice(Math.max(0, currentIndex - 11), currentIndex + 1);
        }
        break;
      default:
        result = [currentPeriod];
    }

    return result;
  };

  // Helper to calculate balance sheet average for multiple periods
  const getBalanceSheetAverage = (data: any, itemName: string, periods: string[]): number | null => {
    if (!data?.line_items) return null;
    const lineItem = data.line_items.find((item: any) => item.name === itemName);
    if (!lineItem?.values) return null;

    // Check if we have sufficient periods for the calculation
    let requiredPeriods;
    if (periodType === 'LQA' && viewType === 'quarterly' && selectedProject?.period_type === 'monthly') {
      // For quarterly view with monthly data, allow partial quarters (at least 1 month)
      requiredPeriods = 1;
    } else {
      requiredPeriods = periodType === 'L3MA' ? 3 : periodType === 'LQA' ? 3 : periodType === 'LTM' ? 12 : 1;
    }

    if (periods.length < requiredPeriods) {
      return null; // Will display as 'n/a'
    }

    let sum = 0;
    let count = 0;
    
    periods.forEach(period => {
      const periodIndex = data.periods.indexOf(period);
      if (periodIndex !== -1 && lineItem.values[periodIndex] != null) {
        sum += lineItem.values[periodIndex];
        count++;
      }
    });

    return count > 0 ? sum / count : null;
  };

  // Helper to calculate P&L sum for multiple periods with annualization
  const getPLAnnualized = (data: any, itemName: string, periods: string[]): number | null => {
    if (!data?.line_items) return null;
    const lineItem = data.line_items.find((item: any) => item.name === itemName);
    if (!lineItem?.values) return null;

    // Check if we have sufficient periods for the calculation
    let requiredPeriods;
    if (periodType === 'LQA' && viewType === 'quarterly' && selectedProject?.period_type === 'monthly') {
      // For quarterly view with monthly data, allow partial quarters (at least 1 month)
      requiredPeriods = 1;
    } else {
      requiredPeriods = periodType === 'L3MA' ? 3 : periodType === 'LQA' ? 3 : periodType === 'LTM' ? 12 : 1;
    }

    if (periods.length < requiredPeriods) {
      return null; // Will display as 'n/a'
    }

    let sum = 0;
    let count = 0;
    
    periods.forEach(period => {
      const periodIndex = data.periods.indexOf(period);
      if (periodIndex !== -1 && lineItem.values[periodIndex] != null) {
        sum += lineItem.values[periodIndex];
        count++;
      }
    });

    if (count === 0) return null;

    // Apply annualization factor based on periodType and backend data type
    let annualizationFactor: number;

    if (selectedProject?.period_type === 'monthly') {
      // Monthly backend data - use existing logic
      switch (periodType) {
        case 'LMA': annualizationFactor = 12; break; // 1 month × 12
        case 'L3MA': annualizationFactor = 4; break; // 3 months × 4
        case 'LQA': annualizationFactor = 4; break;  // 3 months × 4
        case 'LTM': annualizationFactor = 1; break;  // 12 months (already annual)
        default: annualizationFactor = 1;
      }
    } else if (selectedProject?.period_type === 'quarterly') {
      // Quarterly backend data
      switch (periodType) {
        case 'LQA': annualizationFactor = 4; break;  // 1 quarter × 4
        case 'LTM': annualizationFactor = 1; break;  // 4 quarters (already annual)
        default: annualizationFactor = 1;
      }
    } else if (selectedProject?.period_type === 'yearly') {
      // Yearly backend data - no annualization needed
      annualizationFactor = 1;
    } else {
      // Fallback to monthly logic
      switch (periodType) {
        case 'LMA': annualizationFactor = 12; break;
        case 'L3MA': annualizationFactor = 4; break;
        case 'LQA': annualizationFactor = 4; break;
        case 'LTM': annualizationFactor = 1; break;
        default: annualizationFactor = 1;
      }
    }

    return sum * annualizationFactor;
  };

  // Helper functions for NWC calculations (copied from UnifiedDetailedNetWorkingCapital)
  const getDaysInMonth = (monthStr: string) => {
    const [month, year] = monthStr.split('-');
    const monthMap: { [key: string]: number } = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const fullYear = 2000 + parseInt(year);
    return new Date(fullYear, monthMap[month] + 1, 0).getDate();
  };

  // Get months in a period function
  const getMonthsInPeriod = (endMonth: string, viewType: ViewType) => {
    if (viewType === 'monthly') {
      return [endMonth];
    }
  
    const [endMonthStr, yearStr] = endMonth.split('-');
    const year = parseInt(yearStr);
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                       .indexOf(endMonthStr);
  
    let startMonthIndex;
    if (viewType === 'quarterly') {
      startMonthIndex = monthIndex - (monthIndex % 3);
    } else { // yearly
      startMonthIndex = 0;
    }
  
    return availableMonths.filter((month: string) => {
      const [monthStr, monthYearStr] = month.split('-');
      const monthYear = parseInt(monthYearStr);
      const monthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                       .indexOf(monthStr);
      
      if (monthYear !== year) return false;
      return monthIdx >= startMonthIndex && monthIdx <= monthIndex;
    });
  };

  // Function to get actual days in a period - period-aware
  const getPeriodDays = (endMonth: string, periodType?: string) => {
    const projectPeriodType = periodType || selectedProject?.period_type || 'monthly';
    
    if (projectPeriodType === 'monthly' || viewType === 'monthly') {
      return getDaysInMonth(endMonth);
    } else if (projectPeriodType === 'quarterly' || viewType === 'quarterly') {
      // For quarterly, determine the quarter from the month and calculate quarter days
      const [monthStr, yearStr] = endMonth.split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
      const quarterNum = Math.floor(monthIndex / 3) + 1;
      
      // Calculate days for each month in the quarter
      const quarterMonths = getMonthsInQuarter(quarterNum);
      return quarterMonths.reduce((quarterDays, month) => {
        return quarterDays + new Date(2000 + parseInt(yearStr), month + 1, 0).getDate();
      }, 0);
    } else { // annual
      // For annual, use the full year
      const [, yearStr] = endMonth.split('-');
      const year = 2000 + parseInt(yearStr);
      // Check if leap year
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      return isLeapYear ? 366 : 365;
    }
  };

  // Helper function to get month indices for a given quarter
  const getMonthsInQuarter = (quarterNum: number): number[] => {
    switch (quarterNum) {
      case 1: return [0, 1, 2]; // Jan, Feb, Mar
      case 2: return [3, 4, 5]; // Apr, May, Jun
      case 3: return [6, 7, 8]; // Jul, Aug, Sep
      case 4: return [9, 10, 11]; // Oct, Nov, Dec
      default: return [];
    }
  };

  // Get NWC role total from accounts
  const getNWCRoleTotal = (role: string, month: string) => {
    if (!activeTemplate) return 0;
    const matchingCategories = activeTemplate.categories
      .filter(cat => cat.nwcRole === role)
      .map(cat => cat.value);
    
    return matchingCategories.reduce((total, category) => {
      const monthlyAccounts = accounts.filter(account => account.category === category && account.month === month);
      const categoryType = activeTemplate.categories.find(cat => cat.value === category)?.type;
      const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType || '');
      const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType || '');
      
      return total + monthlyAccounts.reduce((total, account) => {
        return total + ((isAsset || isContraAsset) 
          ? account.debit - account.credit 
          : account.credit - account.debit);
      }, 0);
    }, 0);
  };

  // Calculate period total for categories
  const calculatePeriodTotal = (categories: string[], endMonth: string) => {
    const monthsInPeriod = getMonthsInPeriod(endMonth, viewType);
    return monthsInPeriod.reduce((total: number, month: string) => {
      return total + categories.reduce((catTotal, category) => {
        const monthlyAccounts = accounts.filter(account => account.category === category && account.month === month);
        const categoryType = activeTemplate?.categories.find(cat => cat.value === category)?.type;
        const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType || '');
        const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType || '');
        
        return catTotal + monthlyAccounts.reduce((accTotal, account) => {
          return accTotal + ((isAsset || isContraAsset) 
            ? account.debit - account.credit 
            : account.credit - account.debit);
        }, 0);
      }, 0);
    }, 0);
  };


  // --- RESTORED: Helper functions (moved after availableMonths is defined) ---
  const getQuarterEndMonths = () => {
    if (selectedProject?.period_type === 'quarterly') {
      // For quarterly backend data, return all available quarters
      return availablePeriods;
    } else {
      // For monthly backend data, filter to quarter-end months
      const monthMap: Record<string, string> = {
        Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
        Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
      };

      const quarterEndMonths = availablePeriods.filter((month: string) => {
        const [mon] = month.split('-');
        const m = monthMap[mon];
        return ['03', '06', '09', '12'].includes(m);
      });

      const mostRecent = availablePeriods[availablePeriods.length - 1];
      if (quarterEndMonths.length === 0 || quarterEndMonths[quarterEndMonths.length - 1] !== mostRecent) {
        quarterEndMonths.push(mostRecent);
      }
      return quarterEndMonths;
    }
  };

  const getYearEndMonths = () => {
    const monthMap: Record<string, string> = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };
    
    if (selectedProject?.period_type === 'yearly') {
      // For yearly backend data, return all available years
      return availablePeriods;
    }

    // For monthly/quarterly backend data, filter to year-end periods
    const decemberMonths = availablePeriods.filter((month: string) => {
      const [mon] = month.split('-');
      return monthMap[mon] === '12';
    });

    const mostRecent = availablePeriods[availablePeriods.length - 1];
    if (decemberMonths.length === 0 || decemberMonths[decemberMonths.length - 1] !== mostRecent) {
      decemberMonths.push(mostRecent);
    }
    return decemberMonths;
  };

  const calculateRatio = (
    numerator: number | null | undefined,
    denominator: number | null | undefined,
    formula: string,
    numeratorLabel: string,
    denominatorLabel: string
  ) => {
    if (
      denominator === 0 ||
      denominator == null ||
      numerator == null
    ) {
      return { value: 0, formula, calculation: 'n/a' };
    }
    const value = numerator / denominator;
    const calculation = `${numeratorLabel}: ${numerator.toLocaleString()}` +
      `\n${denominatorLabel}: ${denominator.toLocaleString()}` +
      `\n${formula} = ${value.toFixed(3)}`;
    return { value, formula, calculation };
  };

  // --- RESTORED: Helper functions moved outside callback for UI access ---
  const getVal = (lineItem: string, period: string): number | null => {
    return getLineItemValueHelper(lineItem, period, currentDataSource) || null;
  };

  const getReportForLineItem = (lineItem: string) => {
    return (
      currentDataSource?.pnl_detail?.data?.[lineItem] ? currentDataSource.pnl_detail :
      currentDataSource?.bs_detail?.data?.[lineItem] ? currentDataSource.bs_detail :
      currentDataSource?.cf_detail?.data?.[lineItem] ? currentDataSource.cf_detail :
      null
    );
  };

  const getAnnualized = (lineItem: string, period: string, viewType: ViewType) => {
    // Helper to determine if a line item is a P&L item
    const isPLItem = !!currentDataSource?.pnl_detail?.data?.[lineItem];
    const report = getReportForLineItem(lineItem);
    if (!report) return null;
    
    const months = Object.keys(report.data[lineItem] || {}).sort(compareMonthYear);
    const periodIdx = months.indexOf(period);
    
    // Only annualize for P&L items
    if (isPLItem) {
      if (periodIdx === -1) return null;
      const value = report.data[lineItem][period];
      if (value == null) return null;
      
      // Get the project's period type for proper annualization
      const projectPeriodType = selectedProject?.period_type || 'monthly';
      
      // Calculate annualization factor based on period type and view type
      let annualizationFactor: number;
      
      if (projectPeriodType === 'annual') {
        // Annual data is already annualized
        annualizationFactor = 1;
      } else if (projectPeriodType === 'quarterly') {
        // Quarterly data: 4 quarters = 1 year
        annualizationFactor = viewType === 'quarterly' ? 4 : 1;
      } else { // monthly
        // Monthly data: 12 months = 1 year
        annualizationFactor = viewType === 'monthly' ? 12 : 1;
      }
      
      return value * annualizationFactor;
    } else {
      // Balance sheet or other point-in-time items: just return the value for the period
      return report.data[lineItem][period] ?? null;
    }
  };

  // Note: getNWCVal function removed as it's unused in current implementation


  // Memoize the frontend services data to prevent infinite re-calculations
  const frontendServicesData = useMemo(() => {
    if (calculationSource === 'frontend' && activeTemplate && accounts.length > 0) {
      return {
        plData: FrontendPLCalculationService.buildPLData(accounts, activeTemplate, 'monthly', selectedProject),
        bsData: FrontendBSCalculationService.buildBSData(accounts, activeTemplate, 'monthly', selectedProject),
        cfData: FrontendCFCalculationService.buildCFData(accounts, activeTemplate, 'monthly', selectedProject)
      };
    }
    return null;
  }, [calculationSource, activeTemplate, accounts, selectedProject]);

  // Get available periods - period-type aware data source
  const availablePeriods = useMemo(() => {
    if (selectedProject?.period_type === 'monthly') {
      // For monthly backend data, use all monthly periods (preserves existing logic)
      return frontendServicesData?.plData?.periods || [];
    } else if (selectedProject?.period_type === 'quarterly') {
      // For quarterly backend data, build quarterly data and use those periods
      const quarterlyData = FrontendPLCalculationService.buildPLData(accounts, activeTemplate, 'quarterly', selectedProject);
      return quarterlyData?.periods || [];
    } else if (selectedProject?.period_type === 'yearly') {
      // For yearly backend data, build yearly data and use those periods
      const yearlyData = FrontendPLCalculationService.buildPLData(accounts, activeTemplate, 'yearly', selectedProject);
      return yearlyData?.periods || [];
    }
    // Fallback to monthly
    return frontendServicesData?.plData?.periods || [];
  }, [frontendServicesData, selectedProject?.period_type, accounts, activeTemplate]);

  const getPeriods = () => {
    if (viewType === 'monthly') return availablePeriods;
    if (viewType === 'quarterly') return getQuarterEndMonths();
    if (viewType === 'yearly') return getYearEndMonths();
    return [];
  };

  // --- RESTORED: Use advanced period processing ---
  const calculatedPeriods = getPeriods();
  const finalPeriods = calculatedPeriods.length > 0 ? calculatedPeriods : availablePeriods;

  const getMetrics = (period: string) => {
    if (calculationSource === 'frontend' && frontendRatiosData && frontendServicesData) {
      // Use memoized frontend calculation services for consistent metrics
      const { plData, bsData, cfData } = frontendServicesData;

      // Get the periods to use for calculation based on periodType
      const calculationPeriods = getCalculationPeriods(period);


      // Helper to get line item value from frontend data (single period)
      const getLineItemValue = (data: any, itemName: string, singlePeriod: string): number | null => {
        if (!data?.line_items) return null;
        const lineItem = data.line_items.find((item: any) => item.name === itemName);
        const periodIndex = data.periods.indexOf(singlePeriod);
        return lineItem?.values?.[periodIndex] ?? null;
      };

      return {
        // Balance Sheet items for LIQUIDITY ratios - ALWAYS use spot values (current period only)
        currentAssets: getLineItemValue(bsData, 'Total Current Assets', period),
        currentLiabilities: getLineItemValue(bsData, 'Total Current Liabilities', period),
        cash: getLineItemValue(bsData, 'Total Cash', period) || getLineItemValue(bsData, 'Cash', period),
        shortTermInvestments: getLineItemValue(bsData, 'Short-Term Investments', period),

        // Balance Sheet items for LEVERAGE ratios - ALWAYS use spot values (current period only)
        shortTermDebt: getLineItemValue(bsData, 'Short-term Debt', period) ||
          getLineItemValue(bsData, 'Total Short-term Debt', period) ||
          getLineItemValue(bsData, 'Total Current Liabilities', period) || 0,
        longTermDebt: getLineItemValue(bsData, 'Long-term Debt', period) ||
          getLineItemValue(bsData, 'Total Long-term Debt', period) ||
          getLineItemValue(bsData, 'Total Non-current Liabilities', period) || 0,
        totalLiabilities: getLineItemValue(bsData, 'Total Liabilities', period),
        leverageEquity: getLineItemValue(bsData, 'Total Equity', period),

        // Balance Sheet items for PROFITABILITY ratios - use averaging for L3MA, LQA, and LTM, current period for LMA
        totalAssets: periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM'
          ? getBalanceSheetAverage(bsData, 'Total Assets', calculationPeriods)
          : getLineItemValue(bsData, 'Total Assets', period),
        totalEquity: periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM'
          ? getBalanceSheetAverage(bsData, 'Total Equity', calculationPeriods)
          : getLineItemValue(bsData, 'Total Equity', period),

        // Balance Sheet items for CASH CONVERSION CYCLE - use averaging for L3MA, LQA, and LTM, current period for LMA
        inventory: periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM'
          ? getBalanceSheetAverage(bsData, 'Inventory', calculationPeriods)
          : getLineItemValue(bsData, 'Inventory', period),
        accountsReceivable: periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM'
          ? getBalanceSheetAverage(bsData, 'Total Trade Accounts Receivable', calculationPeriods)
          : getLineItemValue(bsData, 'Total Trade Accounts Receivable', period),
        accountsPayable: periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM'
          ? getBalanceSheetAverage(bsData, 'Trade Accounts Payable', calculationPeriods)
          : getLineItemValue(bsData, 'Trade Accounts Payable', period),
        
        // P&L items - use new annualization logic
        totalRevenue: getPLAnnualized(plData, 'Total Revenue', calculationPeriods),
        netRevenue: getPLAnnualized(plData, 'Net Revenue', calculationPeriods),
        grossProfit: getPLAnnualized(plData, 'Gross Margin', calculationPeriods),
        operatingIncome: getPLAnnualized(plData, 'Operating Income', calculationPeriods),
        netIncome: getPLAnnualized(plData, 'Net Income', calculationPeriods),
        
        // EBITDA and Interest Expense need special handling
        ebitda: (() => {
          // Check if we have sufficient periods for the calculation
          let requiredPeriods;
          if (periodType === 'LQA' && viewType === 'quarterly' && selectedProject?.period_type === 'monthly') {
            // For quarterly view with monthly data, allow partial quarters (at least 1 month)
            requiredPeriods = 1;
          } else {
            requiredPeriods = periodType === 'L3MA' ? 3 : periodType === 'LQA' ? 3 : periodType === 'LTM' ? 12 : 1;
          }

          if (calculationPeriods.length < requiredPeriods) {
            return null;
          }

          let sum = 0;
          let count = 0;
          calculationPeriods.forEach(calcPeriod => {
            const periodIndex = plData?.periods.indexOf(calcPeriod) ?? -1;
            if (periodIndex !== -1) {
              const ebitdaValue = FrontendRatiosCalculationService.calculateEBITDA(plData, bsData, calcPeriod, periodIndex);
              if (ebitdaValue != null) {
                sum += ebitdaValue;
                count++;
              }
            }
          });
          if (count === 0) return null;
          
          // Apply annualization (LTM already sums 12 months, no additional annualization needed)
          let annualizationFactor: number;
          switch (periodType) {
            case 'LMA': annualizationFactor = 12; break;
            case 'L3MA': annualizationFactor = 4; break;
            case 'LQA': annualizationFactor = 4; break;
            case 'LTM': annualizationFactor = 1; break; // Already covers full year
            default: annualizationFactor = 1;
          }
          
          return sum * annualizationFactor;
        })(),
        
        interestExpense: (() => {
          // Check if we have sufficient periods for the calculation
          let requiredPeriods;
          if (periodType === 'LQA' && viewType === 'quarterly' && selectedProject?.period_type === 'monthly') {
            // For quarterly view with monthly data, allow partial quarters (at least 1 month)
            requiredPeriods = 1;
          } else {
            requiredPeriods = periodType === 'L3MA' ? 3 : periodType === 'LQA' ? 3 : periodType === 'LTM' ? 12 : 1;
          }

          if (calculationPeriods.length < requiredPeriods) {
            return null;
          }

          let sum = 0;
          let count = 0;
          calculationPeriods.forEach(calcPeriod => {
            const periodIndex = plData?.periods.indexOf(calcPeriod) ?? -1;
            if (periodIndex !== -1) {
              const interestValue = FrontendRatiosCalculationService.calculateInterestExpense(plData, calcPeriod, periodIndex);
              if (interestValue != null) {
                sum += Math.abs(interestValue);
                count++;
              }
            }
          });
          if (count === 0) return 0;
          
          // Apply annualization (LTM already sums 12 months, no additional annualization needed)
          let annualizationFactor: number;
          switch (periodType) {
            case 'LMA': annualizationFactor = 12; break;
            case 'L3MA': annualizationFactor = 4; break;
            case 'LQA': annualizationFactor = 4; break;
            case 'LTM': annualizationFactor = 1; break; // Already covers full year
            default: annualizationFactor = 1;
          }
          
          return sum * annualizationFactor;
        })(),
        
        // Cash Flow items - for now, use current period (may need adjustment)
        operatingCashFlow: FrontendRatiosCalculationService.getOperatingCashFlow(cfData, period),
      };
    }

    // No backend fallback - frontend calculation is required
    return null;
  };

  // Generate NWC service data for ratio calculations
  const nwcServiceData = React.useMemo(() => {
    if (!activeTemplate || accounts.length === 0) return null;
    return FrontendNWCCalculationService.calculate(accounts, activeTemplate, 'monthly');
  }, [accounts, activeTemplate]);

  // Helper function to calculate monthly total for a category
  const calculateMonthlyTotal = (category: string, period: string): number => {
    const monthlyAccounts = accounts.filter(account => account.category === category && account.month === period);
    return monthlyAccounts.reduce((total, account) => {
      return total + (account.debit - account.credit);
    }, 0);
  };

  // Helper function to get actual days in period(s)
  const getActualDaysForPeriods = (periods: string[]): number => {
    return periods.reduce((totalDays, period) => {
      return totalDays + getDaysInMonth(period);
    }, 0);
  };

  // Period-type-aware NWC calculations for L3MA and LTM support
  const calculatePeriodAwareNWCMetric = (metricName: string, period: string): number | null => {
    if (!activeTemplate) return 0;
    
    const calculationPeriods = getCalculationPeriods(period);
    
    // For insufficient periods, return null for n/a display
    let requiredPeriods;
    if (periodType === 'LQA' && viewType === 'quarterly' && selectedProject?.period_type === 'monthly') {
      // For quarterly view with monthly data, allow partial quarters (at least 1 month)
      requiredPeriods = 1;
    } else {
      requiredPeriods = periodType === 'L3MA' ? 3 : periodType === 'LQA' ? 3 : periodType === 'LTM' ? 12 : 1;
    }

    if ((periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM') && calculationPeriods.length < requiredPeriods) {
      return null; // Will display as 'n/a'
    }
    
    if (metricName === 'Days Inventory Outstanding (DIO)') {
      return calculatePeriodAwareDIO(calculationPeriods);
    } else if (metricName === 'Days Sales Outstanding (DSO)') {
      return calculatePeriodAwareDSO(calculationPeriods);
    } else if (metricName === 'Days Payable Outstanding (DPO)') {
      return calculatePeriodAwareDPO(calculationPeriods);
    } else if (metricName === 'Cash Conversion Cycle') {
      const dio = calculatePeriodAwareDIO(calculationPeriods);
      const dso = calculatePeriodAwareDSO(calculationPeriods);
      const dpo = calculatePeriodAwareDPO(calculationPeriods);
      return dio + dso - dpo;
    }
    
    return 0;
  };

  const calculatePeriodAwareDIO = (periods: string[]): number => {
    if (!activeTemplate) return 0;
    
    const inventoryCategories = activeTemplate.categories
      .filter(cat => cat.nwcRole === 'inventory')
      .map(cat => cat.value);
      
    const cogsCategories = activeTemplate.categories
      .filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs')
      .map(cat => cat.value);
    
    if (periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM') {
      // Average inventory over the periods (3 months for L3MA, 12 months for LTM)
      const avgInventory = periods.reduce((sum, p) => {
        const inv = inventoryCategories.reduce((total, category) => {
          return total + calculateMonthlyTotal(category, p);
        }, 0);
        return sum + inv;
      }, 0) / periods.length;
      
      // Sum COGS over the periods
      const totalCOGS = periods.reduce((sum, p) => {
        const cogs = cogsCategories.reduce((total, category) => {
          return total + Math.abs(calculateMonthlyTotal(category, p)); // Use Math.abs to ensure positive
        }, 0);
        return sum + cogs;
      }, 0);
      
      // Get actual days for the periods
      const actualDays = getActualDaysForPeriods(periods);
      
      // For LTM, don't annualize (already 12 months). For L3MA and LQA, annualize.
      if (periodType === 'LTM') {
        // LTM: already covers full year, no annualization needed
        return totalCOGS !== 0 ? (avgInventory / totalCOGS) * actualDays : 0;
      } else if (periodType === 'LQA') {
        // LQA: Use actual quarter days (e.g., 31+28+31 = 90 days for Q1)
        const actualDays = getActualDaysForPeriods(periods);
        return totalCOGS !== 0 ? (avgInventory / totalCOGS) * actualDays : 0;
      } else {
        // L3MA: Annualize: (total COGS / actual days) * 365 = daily COGS rate * 365 days
        const annualizedCOGS = totalCOGS !== 0 ? (totalCOGS / actualDays) * 365 : 0;
        return annualizedCOGS !== 0 ? (avgInventory / annualizedCOGS) * 365 : 0;
      }
    } else {
      // LMA: Use single period values, annualize COGS (×12)
      const period = periods[0];
      const inventory = inventoryCategories.reduce((total, category) => {
        return total + calculateMonthlyTotal(category, period);
      }, 0);
      const monthlyCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, period)); // Use Math.abs to ensure positive
      }, 0);
      const monthDays = getDaysInMonth(period);
      
      // Annualize: (monthly COGS / days in month) * 365 = daily COGS rate * 365 days
      const annualizedCOGS = monthlyCOGS !== 0 ? (monthlyCOGS / monthDays) * 365 : 0;
      
      return annualizedCOGS !== 0 ? (inventory / annualizedCOGS) * 365 : 0;
    }
  };

  const calculatePeriodAwareDSO = (periods: string[]): number => {
    if (!activeTemplate) return 0;
    
    const revenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue')
      .map(cat => cat.value);
    
    if (periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM') {
      // Average accounts receivable over the periods (3 months for L3MA, 12 months for LTM)
      const avgAR = periods.reduce((sum, p) => {
        const ar = getNWCRoleTotal('accounts-receivable', p);
        return sum + ar;
      }, 0) / periods.length;
      
      // Sum revenue over the periods
      const totalRevenue = periods.reduce((sum, p) => {
        const rev = revenueCategories.reduce((total, category) => {
          return total + Math.abs(calculateMonthlyTotal(category, p)); // Use Math.abs to ensure positive
        }, 0);
        return sum + rev;
      }, 0);
      
      // Get actual days for the periods
      const actualDays = getActualDaysForPeriods(periods);
      
      // For LTM, don't annualize (already 12 months). For L3MA and LQA, annualize.
      if (periodType === 'LTM') {
        // LTM: already covers full year, no annualization needed
        return totalRevenue !== 0 ? (avgAR / totalRevenue) * actualDays : 0;
      } else if (periodType === 'LQA') {
        // LQA: Use actual quarter days (e.g., 31+28+31 = 90 days for Q1)
        const actualDays = getActualDaysForPeriods(periods);
        return totalRevenue !== 0 ? (avgAR / totalRevenue) * actualDays : 0;
      } else {
        // L3MA: Annualize: (total revenue / actual days) * 365 = daily revenue rate * 365 days
        const annualizedRevenue = totalRevenue !== 0 ? (totalRevenue / actualDays) * 365 : 0;
        return annualizedRevenue !== 0 ? (avgAR / annualizedRevenue) * 365 : 0;
      }
    } else {
      // LMA: Use single period values, annualize revenue (×12)
      const period = periods[0];
      const accountsReceivable = getNWCRoleTotal('accounts-receivable', period);
      const monthlyRevenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, period)); // Use Math.abs to ensure positive
      }, 0);
      const monthDays = getDaysInMonth(period);
      
      // Annualize: (monthly revenue / days in month) * 365 = daily revenue rate * 365 days
      const annualizedRevenue = monthlyRevenue !== 0 ? (monthlyRevenue / monthDays) * 365 : 0;
      
      return annualizedRevenue !== 0 ? (accountsReceivable / annualizedRevenue) * 365 : 0;
    }
  };

  const calculatePeriodAwareDPO = (periods: string[]): number => {
    if (!activeTemplate) return 0;
    
    const cogsCategories = activeTemplate.categories
      .filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs')
      .map(cat => cat.value);
    
    if (periodType === 'L3MA' || periodType === 'LQA' || periodType === 'LTM') {
      // Average accounts payable over the periods (3 months for L3MA, 12 months for LTM)
      const avgAP = periods.reduce((sum, p) => {
        const ap = getNWCRoleTotal('accounts-payable', p);
        return sum + ap;
      }, 0) / periods.length;
      
      // Sum COGS over the periods
      const totalCOGS = periods.reduce((sum, p) => {
        const cogs = cogsCategories.reduce((total, category) => {
          return total + Math.abs(calculateMonthlyTotal(category, p)); // Use Math.abs to ensure positive
        }, 0);
        return sum + cogs;
      }, 0);
      
      // Get actual days for the periods
      const actualDays = getActualDaysForPeriods(periods);
      
      // For LTM, don't annualize (already 12 months). For L3MA and LQA, annualize.
      if (periodType === 'LTM') {
        // LTM: already covers full year, no annualization needed
        return totalCOGS !== 0 ? (avgAP / totalCOGS) * actualDays : 0;
      } else if (periodType === 'LQA') {
        // LQA: Use actual quarter days (e.g., 31+28+31 = 90 days for Q1)
        const actualDays = getActualDaysForPeriods(periods);
        return totalCOGS !== 0 ? (avgAP / totalCOGS) * actualDays : 0;
      } else {
        // L3MA: Annualize: (total COGS / actual days) * 365 = daily COGS rate * 365 days
        const annualizedCOGS = totalCOGS !== 0 ? (totalCOGS / actualDays) * 365 : 0;
        return annualizedCOGS !== 0 ? (avgAP / annualizedCOGS) * 365 : 0;
      }
    } else {
      // LMA: Use single period values, annualize COGS (×12)
      const period = periods[0];
      const accountsPayable = getNWCRoleTotal('accounts-payable', period);
      const monthlyCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, period)); // Use Math.abs to ensure positive
      }, 0);
      const monthDays = getDaysInMonth(period);
      
      // Annualize: (monthly COGS / days in month) * 365 = daily COGS rate * 365 days
      const annualizedCOGS = monthlyCOGS !== 0 ? (monthlyCOGS / monthDays) * 365 : 0;
      
      return annualizedCOGS !== 0 ? (accountsPayable / annualizedCOGS) * 365 : 0;
    }
  };

  // FRONTEND CALCULATION: Calculate all ratios here (single source of truth)
  const calculateAllRatios = useCallback(() => {
    if (!currentDataSource || finalPeriods.length === 0) return null;

    const ratioPeriods = finalPeriods;
    const line_items: RatioLineItem[] = [];

    // Define ratio calculations with enhanced metadata
    const ratioDefinitions = [
      // Liquidity Ratios Section
      {
        name: 'Current Ratio',
        formula: 'Total Current Assets ÷ Total Current Liabilities',
        description: 'Measures the company\'s ability to pay short-term obligations.',
        account_mappings: ['Total Current Assets', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => m.currentLiabilities === 0 || m.currentLiabilities === null ? null : m.currentAssets / m.currentLiabilities
      },
      {
        name: 'Quick Ratio (Acid-Test)',
        formula: '(Cash + Short-Term Investments + Trade Accounts Receivable) ÷ Total Current Liabilities',
        description: 'Measures liquidity excluding inventory.',
        account_mappings: ['Cash', 'Short-Term Investments', 'Trade Accounts Receivable', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => {
          if (m.currentLiabilities === 0 || m.currentLiabilities === null) return null;
          const quickAssets = (m.cash || 0) + (m.shortTermInvestments || 0) + (m.accountsReceivable || 0);
          return quickAssets / m.currentLiabilities;
        }
      },
      {
        name: 'Cash Ratio',
        formula: 'Cash ÷ Total Current Liabilities',
        description: 'Measures the ability to pay short-term debts with cash.',
        account_mappings: ['Cash', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => m.currentLiabilities === 0 || m.currentLiabilities === null ? null : (m.cash || 0) / m.currentLiabilities
      },
      {
        name: 'Operating Cash Flow Ratio',
        formula: 'Operating Cash Flow ÷ Total Current Liabilities',
        description: 'Measures the ability to pay current liabilities with operating cash flow.',
        account_mappings: ['Operating Cash Flow', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => (m.currentLiabilities === 0 || m.currentLiabilities === null || !m.operatingCashFlow) ? null : m.operatingCashFlow / m.currentLiabilities
      },

      // Profitability Ratios Section
      {
        name: 'Gross Profit Margin',
        formula: 'Gross Profit ÷ Net Revenue',
        description: 'Measures gross profitability.',
        account_mappings: ['Gross Profit', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.grossProfit || 0) / m.netRevenue
      },
      {
        name: 'Operating Margin',
        formula: 'Operating Income ÷ Net Revenue',
        description: 'Measures operating profitability.',
        account_mappings: ['Operating Income', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.operatingIncome || 0) / m.netRevenue
      },
      {
        name: 'EBITDA Margin',
        formula: 'EBITDA ÷ Net Revenue',
        description: 'Measures cash profitability before interest, taxes, depreciation, and amortization.',
        account_mappings: ['EBITDA', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.ebitda || 0) / m.netRevenue
      },
      {
        name: 'Net Profit Margin',
        formula: 'Net Income ÷ Net Revenue',
        description: 'Measures overall profitability.',
        account_mappings: ['Net Income', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.netIncome || 0) / m.netRevenue
      },
      {
        name: 'Return on Assets (ROA)',
        formula: 'Net Income ÷ Total Assets',
        description: 'Measures profitability relative to total assets.',
        account_mappings: ['Net Income', 'Total Assets'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.totalAssets === 0 || m.totalAssets === null ? null : (m.netIncome || 0) / m.totalAssets
      },
      {
        name: 'Return on Equity (ROE)',
        formula: 'Net Income ÷ Total Equity',
        description: 'Measures profitability relative to shareholders\' equity.',
        account_mappings: ['Net Income', 'Total Equity'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.totalEquity === 0 || m.totalEquity === null ? null : (m.netIncome || 0) / m.totalEquity
      },

      // Leverage Ratios Section
      {
        name: 'Debt-to-Capital',
        formula: '(Short-term Debt + Long-term Debt) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
        description: 'Measures debt as a percentage of total capital.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const totalCapital = totalDebt + (m.leverageEquity || 0);
          return totalCapital === 0 ? null : totalDebt / totalCapital;
        }
      },
      {
        name: 'Net Debt-to-Capital',
        formula: '(Short-term Debt + Long-term Debt - Cash) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
        description: 'Measures net debt as a percentage of total capital.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const netDebt = grossDebt - (m.cash || 0);
          const totalCapital = grossDebt + (m.leverageEquity || 0);
          return totalCapital === 0 ? null : netDebt / totalCapital;
        }
      },
      {
        name: 'Debt-to-Equity',
        formula: '(Short-term Debt + Long-term Debt) ÷ Total Equity',
        description: 'Measures debt relative to equity.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          if (m.leverageEquity === 0 || m.leverageEquity === null) return null;
          const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          return totalDebt / m.leverageEquity;
        }
      },
      {
        name: 'Net Debt-to-Equity',
        formula: '(Short-term Debt + Long-term Debt - Cash) ÷ Total Equity',
        description: 'Measures net debt relative to equity.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          if (m.leverageEquity === 0 || m.leverageEquity === null) return null;
          const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const netDebt = grossDebt - (m.cash || 0);
          return netDebt / m.leverageEquity;
        }
      },
      {
        name: 'Leverage Ratio',
        formula: '(Short-term Debt + Long-term Debt) ÷ EBITDA',
        description: 'Measures debt relative to earnings.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'EBITDA'],
        category: 'leverage',
        format: 'multiple' as const,
        calc: (m: any) => {
          if (m.ebitda === 0 || m.ebitda === null) return null;
          const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          return totalDebt / m.ebitda;
        }
      },
      {
        name: 'Net Leverage Ratio',
        formula: '(Short-term Debt + Long-term Debt - Cash) ÷ EBITDA',
        description: 'Measures net debt relative to earnings.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'EBITDA'],
        category: 'leverage',
        format: 'multiple' as const,
        calc: (m: any) => {
          if (m.ebitda === 0 || m.ebitda === null) return null;
          const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const netDebt = grossDebt - (m.cash || 0);
          return netDebt / m.ebitda;
        }
      },
      {
        name: 'Interest Coverage Ratio',
        formula: 'EBITDA ÷ Interest Expense',
        description: 'Measures ability to pay interest expenses.',
        account_mappings: ['EBITDA', 'Interest Expense'],
        category: 'leverage',
        format: 'multiple' as const,
        calc: (m: any) => (m.interestExpense === 0 || m.interestExpense === null || m.ebitda === null) ? null : m.ebitda / m.interestExpense
      }
    ];

    // Build the ratios data structure
    const sections = [
      { id: 'liquidity', name: 'Liquidity Ratios', ratios: ratioDefinitions.filter(r => r.category === 'liquidity') },
      { id: 'profitability', name: 'Profitability Ratios', ratios: ratioDefinitions.filter(r => r.category === 'profitability') },
      { id: 'leverage', name: 'Leverage Ratios', ratios: ratioDefinitions.filter(r => r.category === 'leverage') },
      { id: 'cash_conversion', name: 'Cash Conversion Cycle', ratios: [] }
    ];

    sections.forEach(section => {
      // Add section header
      line_items.push({
        name: section.name,
        type: 'section_header',
        level: 1,
        values: ratioPeriods.map(() => null),
        category: section.id
      });

      if (section.id === 'cash_conversion') {
        // Add NWC metrics using FrontendNWCCalculationService for consistency with UnifiedDetailedNetWorkingCapital

        const nwcMetrics = [
          { 
            name: 'Days Inventory Outstanding (DIO)', 
            calc: (period: string) => calculatePeriodAwareNWCMetric('Days Inventory Outstanding (DIO)', period)
          },
          { 
            name: 'Days Sales Outstanding (DSO)', 
            calc: (period: string) => calculatePeriodAwareNWCMetric('Days Sales Outstanding (DSO)', period)
          },
          { 
            name: 'Days Payable Outstanding (DPO)', 
            calc: (period: string) => calculatePeriodAwareNWCMetric('Days Payable Outstanding (DPO)', period)
          },
          { 
            name: 'Cash Conversion Cycle', 
            calc: (period: string) => calculatePeriodAwareNWCMetric('Cash Conversion Cycle', period)
          }
        ];
        
        nwcMetrics.forEach(metric => {
          const values = ratioPeriods.map((period: string) => {
            const calculatedValue = metric.calc(period);
            
            
            return calculatedValue;
          });
          
          line_items.push({
            name: metric.name,
            type: 'ratio',
            level: 1,
            formula: getFormulaForNWCMetric(metric.name),
            description: getDescriptionForNWCMetric(metric.name),
            account_mappings: getAccountMappingsForNWCMetric(metric.name),
            values,
            category: section.id,
            format: 'days' as const
          });
        });
      } else {
        // Add regular financial ratios
        section.ratios.forEach(ratio => {
          const values = ratioPeriods.map((period: string) => {
            const metrics = getMetrics(period);
            const result = ratio.calc(metrics);
            
            // Simple debug for first 3 months only
            const isFirst3Months = ['Jan-23', 'Feb-23', 'Mar-23'].includes(period);
            if (isFirst3Months && ratio.name === 'Gross Profit Margin') {
              console.log(`📊 [RATIO] ${ratio.name} - ${period} (${periodType}): GP=${metrics.grossProfit} / Rev=${metrics.netRevenue} = ${result}`);
            }
            
            return result;
          });

          line_items.push({
            name: ratio.name,
            type: 'ratio',
            level: 1,
            formula: ratio.formula,
            description: ratio.description,
            account_mappings: ratio.account_mappings,
            values,
            category: section.id,
            format: ratio.format
          });
        });
      }
    });

    console.log(`🔍 [CALC] calculateAllRatios triggered with periodType: ${periodType}`);
    return { periods: ratioPeriods, line_items, sections };
  }, [currentDataSource, finalPeriods, calculationSource, frontendRatiosData, frontendServicesData, accounts, activeTemplate, selectedProject, viewType, periodType, nwcServiceData]);

  // Calculate ratios (single source of truth for frontend display)
  // For frontend calculations, always use calculateAllRatios() to respect periodType
  // For backend calculations, use currentDataSource directly
  const ratiosData = calculationSource === 'frontend' ? calculateAllRatios() : (currentDataSource?.line_items ? currentDataSource : { periods: [], line_items: [] });
  const { periods, line_items } = ratiosData || { periods: [], line_items: [] };

  // CCC calculations now use period-aware functions directly for consistency

  // BACKEND SYNC: Sync calculated ratios to backend (following P&L/BS pattern)
  const syncFinRatiosToBackend = useCallback(async () => {
    if (!selectedProject || !activeTemplate || !frontendRatiosData) {
      console.log('[Ratios Sync] Skipping sync - missing required data');
      return;
    }

    try {
      console.log('[Ratios Sync] Starting Financial Ratios sync...');
      
      const supabase = getSupabase();
      
      const { data: existingData, error: selectError } = await supabase
        .from('financial_reports')
        .select('id')
        .eq('project_id', selectedProject.id)
        .eq('report_type', 'fin_ratios')
        .maybeSingle();

      if (selectError) {
        console.error('[Ratios Sync] Error checking existing record:', selectError);
        throw selectError;
      }

      const reportPayload = {
        project_id: selectedProject.id,
        report_type: 'fin_ratios',
        data: frontendRatiosData, // Store the SAME data that's displayed in UI
        template_id: activeTemplate.id,
        updated_at: new Date().toISOString()
      };

      if (existingData) {
        const { error: updateError } = await supabase
          .from('financial_reports')
          .update(reportPayload)
          .eq('id', existingData.id as string);

        if (updateError) {
          console.error('[Ratios Sync] Error updating record:', updateError);
          throw updateError;
        }
      } else {
        const { error: insertError } = await supabase
          .from('financial_reports')
          .insert([{
            ...reportPayload,
            created_at: new Date().toISOString()
          }]);

        if (insertError) {
          console.error('[Ratios Sync] Error inserting record:', insertError);
          throw insertError;
        }
      }
      
      console.log('[Ratios Sync] ✓ Successfully synced Financial Ratios');
      
    } catch (error) {
      console.error('[Ratios Sync] Error syncing Financial Ratios:', error);
    }
  }, [selectedProject, activeTemplate, frontendRatiosData]);

  // Auto-sync when ratios data changes (like P&L/BS does)
  // NOTE: Only sync when accounts or template change, NOT when viewType changes
  // This prevents unnecessary backend writes when user toggles between monthly/quarterly/yearly views
  useEffect(() => {
    if (frontendRatiosData && selectedProject && activeTemplate) {
      // Debounce the sync
      const timer = setTimeout(() => {
        syncFinRatiosToBackend();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [accounts, activeTemplate, selectedProject, syncFinRatiosToBackend]); // Removed frontendRatiosData to avoid viewType triggers

  // Format ratio values for display
  const formatRatioValue = (value: number | null, format: string = 'decimal'): string => {
    if (value === null || value === undefined || isNaN(value)) return 'n/a';
    
    
    switch (format) {
      case 'percentage': return (value * 100).toFixed(1) + '%';
      case 'multiple': return value.toFixed(2) + 'x';
      case 'days': return value.toFixed(1) + ' days';
      case 'decimal':
      default: return value.toFixed(2);
    }
  };


  // Loading and error states
  if (loading && !currentDataSource) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading financial reports...</p>
      </div>
    );
  }

  if (error && !currentDataSource) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!ratiosData || !ratiosData.line_items?.length) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No financial ratios data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      {/* Same styling and UI as before */}
      <style>
        {`
          .financial-ratios-table {
            table-layout: auto;
            border-collapse: separate;
            border-spacing: 0;
          }
          .financial-ratios-table th,
          .financial-ratios-table td {
            white-space: nowrap;
          }
          .financial-ratios-table th:first-child,
          .financial-ratios-table td:first-child {
            position: sticky;
            left: 0;
            background: #f9fafb;
            width: 240px;
            min-width: 240px;
            max-width: 240px;
            white-space: nowrap;
            border-right: 1px solid #e5e7eb;
            text-align: left;
            z-index: 10;
          }
          .financial-ratios-table th:first-child {
            top: 0;
            z-index: 30;
          }
          .financial-ratios-table th {
            position: sticky;
            top: 0;
            z-index: 20;
            background: #f9fafb;
            text-align: center;
          }
          .financial-ratios-table th:not(:first-child),
          .financial-ratios-table td:not(:first-child) {
            width: auto;
            min-width: 160px;
            max-width: 200px;
            text-align: center;
          }
          .financial-ratios-table td {
            text-align: center;
          }
        `}
      </style>

      {/* View toggle only */}
      <div className="w-full flex justify-end mb-4">
        <ViewToggle
          currentView={viewType}
          onViewChange={(newView) => {
            setViewType(newView);
            setPeriodType(periodOptionsByView[newView][0].value as PeriodType);
          }}
          projectPeriodType={selectedProject?.period_type}
        />
      </div>

      {/* Main content area with side panel and table */}
      <div className="flex gap-4">
        {/* Left side panel - Ratio Calculation Convention */}
        <div className="flex-shrink-0 w-64">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Ratio Calculation Convention</h3>
              <span
                className="ml-2 cursor-help"
                title={`LMA: Single month metrics annualized to yearly equivalent (×12)\n\nL3MA: 3-month average metrics annualized to yearly equivalent (×4)\n\nLQA: Quarterly metrics (3 months) annualized to yearly equivalent (×4)\n\nLTM: 12-month trailing metrics (no annualization)`}
              >
                <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </span>
            </div>
            <div className="space-y-2">
              {/* Last Month (Annualized) */}
              <button
                onClick={() => setPeriodType('LMA')}
                disabled={viewType !== 'monthly'}
                className={`w-full px-3 py-2 text-sm font-medium rounded-md transition-colors text-left ${
                  viewType !== 'monthly'
                    ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                    : periodType === 'LMA'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                Last Month (Annualized)
              </button>
              {/* Last 3 Months (Annualized) */}
              <button
                onClick={() => setPeriodType('L3MA')}
                disabled={viewType !== 'monthly'}
                className={`w-full px-3 py-2 text-sm font-medium rounded-md transition-colors text-left ${
                  viewType !== 'monthly'
                    ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                    : periodType === 'L3MA'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                Last 3 Months (Annualized)
              </button>
              {/* Last Quarter (Annualized) */}
              <button
                onClick={() => setPeriodType('LQA')}
                disabled={viewType !== 'quarterly'}
                className={`w-full px-3 py-2 text-sm font-medium rounded-md transition-colors text-left ${
                  viewType !== 'quarterly'
                    ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                    : periodType === 'LQA'
                      ? 'bg-blue-600 text-white shadow'
                      : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                Last Quarter (Annualized)
              </button>
              {/* Last 12 Months */}
              <button
                onClick={() => setPeriodType('LTM')}
                disabled={false} // Always available
                className={`w-full px-3 py-2 text-sm font-medium rounded-md transition-colors text-left ${
                  periodType === 'LTM'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                Last 12 Months
              </button>
            </div>
          </div>
        </div>

        {/* Table container with scroll */}
        <div className="flex-1" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'inline-block', minWidth: 'fit-content' }}>
          <table className="financial-ratios-table min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-10 px-6 py-3 text-left text-sm font-semibold text-black border-r border-b border-gray-200 bg-gray-50">
                  Ratio
                </th>
                {periods.map((period: string) => (
                  <th key={period} className="sticky top-0 z-10 px-6 py-3 text-center text-sm font-semibold text-black border-b border-gray-200 bg-gray-50">
                    {getDateForPeriod(period, accounts)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {line_items.map((item: any, index: number) => {
                if (item.type === 'section_header') {
                  // Section header row
                  const sectionColors: Record<string, string> = {
                    'liquidity': 'bg-blue-100 text-blue-900',
                    'profitability': 'bg-green-100 text-green-900',
                    'leverage': 'bg-orange-100 text-orange-900',
                    'cash_conversion': 'bg-purple-100 text-purple-900'
                  };
                  
                  const sectionColor = sectionColors[item.category || ''] || 'bg-gray-100 text-gray-900';
                  
                  return (
                    <tr key={`section-${index}`} className={sectionColor}>
                      <td className={`sticky left-0 z-10 ${sectionColor} px-6 py-3 text-sm font-bold border-r border-gray-200`}>
                        🔹 {item.name}
                      </td>
                      {periods.map((period: string) => (
                        <td key={period} className={sectionColor}></td>
                      ))}
                    </tr>
                  );
                } else {
                  // Regular ratio row
                  return (
                    <tr key={`ratio-${index}`}>
                      <td className="sticky left-0 z-10 bg-white px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
                        <div className="flex items-center gap-1">
                          <span>{item.name}</span>
                          {item.formula && (
                            <span className="ml-1 cursor-help" title={item.formula}>
                              <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 inline" />
                            </span>
                          )}
                        </div>
                      </td>
                      {periods.map((period: string, periodIndex: number) => {
                        const value = item.values[periodIndex];
                        const formattedValue = formatRatioValue(value, item.format);
                        
                        
                        // Create enhanced tooltip calculation info (restored from old version)
                        let calculation = {
                          formula: item.formula || '',
                          calculation: item.description || item.formula || '',
                          value: value
                        };
                        
                        // For regular ratios, create detailed calculation breakdown
                        if (item.type === 'ratio' && item.category !== 'cash_conversion') {
                          const metrics = getMetrics(period);
                          // Create detailed calculation based on ratio type
                          if (item.name === 'Current Ratio') {
                            calculation = calculateRatio(
                              metrics.currentAssets, 
                              metrics.currentLiabilities, 
                              'Current Assets ÷ Current Liabilities', 
                              'Current Assets', 
                              'Current Liabilities'
                            );
                          } else if (item.name === 'Quick Ratio (Acid-Test)') {
                            const quickAssets = (metrics.cash || 0) + (metrics.shortTermInvestments || 0) + (metrics.accountsReceivable || 0);
                            calculation = calculateRatio(
                              quickAssets, 
                              metrics.currentLiabilities, 
                              '(Cash + Short-Term Investments + Trade Accounts Receivable) ÷ Current Liabilities', 
                              'Quick Assets', 
                              'Current Liabilities'
                            );
                          } else if (item.name === 'Cash Ratio') {
                            calculation = calculateRatio(
                              metrics.cash, 
                              metrics.currentLiabilities, 
                              'Cash ÷ Current Liabilities', 
                              'Cash', 
                              'Current Liabilities'
                            );
                          } else if (item.name === 'Operating Cash Flow Ratio') {
                            calculation = calculateRatio(
                              metrics.operatingCashFlow, 
                              metrics.currentLiabilities, 
                              'Operating Cash Flow ÷ Current Liabilities', 
                              'Operating Cash Flow', 
                              'Current Liabilities'
                            );
                          } else if (item.name === 'Gross Profit Margin') {
                            calculation = calculateRatio(
                              metrics.grossProfit, 
                              metrics.netRevenue, 
                              'Gross Profit ÷ Net Revenue', 
                              'Gross Profit', 
                              'Net Revenue'
                            );
                          } else if (item.name === 'Operating Margin') {
                            calculation = calculateRatio(
                              metrics.operatingIncome, 
                              metrics.netRevenue, 
                              'Operating Income ÷ Net Revenue', 
                              'Operating Income', 
                              'Net Revenue'
                            );
                          } else if (item.name === 'EBITDA Margin') {
                            calculation = calculateRatio(
                              metrics.ebitda, 
                              metrics.netRevenue, 
                              'EBITDA ÷ Net Revenue', 
                              'EBITDA', 
                              'Net Revenue'
                            );
                          } else if (item.name === 'Net Profit Margin') {
                            calculation = calculateRatio(
                              metrics.netIncome, 
                              metrics.netRevenue, 
                              'Net Income ÷ Net Revenue', 
                              'Net Income', 
                              'Net Revenue'
                            );
                          } else if (item.name === 'Return on Assets (ROA)') {
                            calculation = calculateRatio(
                              metrics.netIncome, 
                              metrics.totalAssets, 
                              'Net Income ÷ Total Assets', 
                              'Net Income', 
                              'Total Assets'
                            );
                          } else if (item.name === 'Return on Equity (ROE)') {
                            calculation = calculateRatio(
                              metrics.netIncome, 
                              metrics.totalEquity, 
                              'Net Income ÷ Total Equity', 
                              'Net Income', 
                              'Total Equity'
                            );
                          } else if (item.name === 'Debt-to-Capital') {
                            const numerator = (metrics.shortTermDebt || 0) + (metrics.longTermDebt || 0);
                            const denominator = numerator + (metrics.leverageEquity || 0);
                            calculation = calculateRatio(
                              numerator,
                              denominator,
                              '(Short-term Debt + Long-term Debt) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
                              'Total Debt',
                              'Total Capital'
                            );
                          } else if (item.name === 'Net Debt-to-Capital') {
                            const grossDebt = (metrics.shortTermDebt || 0) + (metrics.longTermDebt || 0);
                            const numerator = grossDebt - (metrics.cash || 0);
                            const denominator = grossDebt + (metrics.leverageEquity || 0);
                            calculation = calculateRatio(
                              numerator,
                              denominator,
                              '(Short-term Debt + Long-term Debt - Cash) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
                              'Net Debt',
                              'Total Capital'
                            );
                          } else if (item.name === 'Debt-to-Equity') {
                            const numerator = (metrics.shortTermDebt || 0) + (metrics.longTermDebt || 0);
                            calculation = calculateRatio(
                              numerator,
                              metrics.leverageEquity,
                              '(Short-term Debt + Long-term Debt) ÷ Total Equity',
                              'Total Debt',
                              'Total Equity'
                            );
                          } else if (item.name === 'Net Debt-to-Equity') {
                            const grossDebt = (metrics.shortTermDebt || 0) + (metrics.longTermDebt || 0);
                            const numerator = grossDebt - (metrics.cash || 0);
                            calculation = calculateRatio(
                              numerator,
                              metrics.leverageEquity,
                              '(Short-term Debt + Long-term Debt - Cash) ÷ Total Equity',
                              'Net Debt',
                              'Total Equity'
                            );
                          } else if (item.name === 'Leverage Ratio') {
                            const numerator = (metrics.shortTermDebt || 0) + (metrics.longTermDebt || 0);
                            calculation = calculateRatio(
                              numerator, 
                              metrics.ebitda, 
                              '(Short-term Debt + Long-term Debt) ÷ EBITDA', 
                              'Total Debt', 
                              'EBITDA'
                            );
                          } else if (item.name === 'Net Leverage Ratio') {
                            const grossDebt = (metrics.shortTermDebt || 0) + (metrics.longTermDebt || 0);
                            const numerator = grossDebt - (metrics.cash || 0);
                            calculation = calculateRatio(
                              numerator, 
                              metrics.ebitda, 
                              '(Short-term Debt + Long-term Debt - Cash) ÷ EBITDA', 
                              'Net Debt', 
                              'EBITDA'
                            );
                          } else if (item.name === 'Interest Coverage Ratio') {
                            calculation = calculateRatio(
                              metrics.ebitda, 
                              metrics.interestExpense, 
                              'EBITDA ÷ Interest Expense', 
                              'EBITDA', 
                              'Interest Expense'
                            );
                          }
                        } else if (item.type === 'ratio' && item.category === 'cash_conversion') {
                          // Use period-aware calculations for consistency with display values
                          const dio = calculatePeriodAwareNWCMetric('Days Inventory Outstanding (DIO)', period);
                          const dso = calculatePeriodAwareNWCMetric('Days Sales Outstanding (DSO)', period);
                          const dpo = calculatePeriodAwareNWCMetric('Days Payable Outstanding (DPO)', period);
                          const ccc = dio !== null && dso !== null && dpo !== null ? dio + dso - dpo : null;
                          
                          if (item.name === 'Days Inventory Outstanding (DIO)') {
                            calculation = {
                              formula: 'Inventory ÷ COGS × Days',
                              calculation: `Period: ${period}\nDIO = ${dio?.toFixed(1) || 'n/a'} days`,
                              value: dio
                            };
                          } else if (item.name === 'Days Sales Outstanding (DSO)') {
                            calculation = {
                              formula: 'Accounts Receivable ÷ Revenue × Days',
                              calculation: `Period: ${period}\nDSO = ${dso?.toFixed(1) || 'n/a'} days`,
                              value: dso
                            };
                          } else if (item.name === 'Days Payable Outstanding (DPO)') {
                            calculation = {
                              formula: 'Accounts Payable ÷ COGS × Days',
                              calculation: `Period: ${period}\nDPO = ${dpo?.toFixed(1) || 'n/a'} days`,
                              value: dpo
                            };
                          } else if (item.name === 'Cash Conversion Cycle') {
                            calculation = {
                              formula: 'DIO + DSO - DPO',
                              calculation: `Period: ${period}\nCCC = ${ccc?.toFixed(1) || 'n/a'} days\n\nBreakdown:\nDIO: ${dio?.toFixed(1)} days\nDSO: ${dso?.toFixed(1)} days\nDPO: ${dpo?.toFixed(1)} days`,
                              value: ccc
                            };
                          }
                        }
                        
                        return (
                          <td key={period} className="text-center text-sm text-gray-900">
                            {formattedValue}
                            {item.formula && (
                              <RatioTooltip calculation={calculation} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 text-sm text-gray-600">
        <div className="flex items-center">
          <HelpCircle className="h-4 w-4 mr-1" />
          <span>Hover over the help icon for detailed calculation breakdown</span>
        </div>
      </div>
    </div>
  );
};

// Helper functions for NWC metrics
function getFormulaForNWCMetric(metricName: string): string {
  const formulas: Record<string, string> = {
    'Days Inventory Outstanding (DIO)': 'Inventory ÷ COGS × Days',
    'Days Sales Outstanding (DSO)': 'Accounts Receivable ÷ Revenue × Days',
    'Days Payable Outstanding (DPO)': 'Accounts Payable ÷ COGS × Days',
    'Cash Conversion Cycle': 'DIO + DSO - DPO'
  };
  return formulas[metricName] || '';
}

function getDescriptionForNWCMetric(metricName: string): string {
  const descriptions: Record<string, string> = {
    'Days Inventory Outstanding (DIO)': 'Average number of days inventory is held',
    'Days Sales Outstanding (DSO)': 'Average number of days to collect receivables',
    'Days Payable Outstanding (DPO)': 'Average number of days to pay suppliers',
    'Cash Conversion Cycle': 'Time to convert investments into cash flows'
  };
  return descriptions[metricName] || '';
}

function getAccountMappingsForNWCMetric(metricName: string): string[] {
  const mappings: Record<string, string[]> = {
    'Days Inventory Outstanding (DIO)': ['Inventory', 'COGS'],
    'Days Sales Outstanding (DSO)': ['Accounts Receivable', 'Revenue'],
    'Days Payable Outstanding (DPO)': ['Accounts Payable', 'COGS'],
    'Cash Conversion Cycle': ['DIO', 'DSO', 'DPO']
  };
  return mappings[metricName] || [];
}

export default FinancialRatios;